import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { authAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, Trophy, Award, Plus, Trash2, Pencil,
  ChevronRight, Crown, Flame, Star, ArrowLeft, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import PremiumModal from '../components/PremiumModal';

const API = process.env.REACT_APP_BACKEND_URL;

// Avatar options
const AVATARS = ["🧑", "👩", "👨", "👧", "👦", "🧒", "👵", "👴", "🦊", "🐻", "🐼", "🦁", "🐯", "🐸", "🦉", "🐙"];

const FamilyPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [badges, setBadges] = useState([]);
  const [stats, setStats] = useState({});
  const [canAddProfile, setCanAddProfile] = useState(false);
  const [activeTab, setActiveTab] = useState('members');
  const [rankingTimeframe, setRankingTimeframe] = useState('weekly_xp');
  const [showAddProfile, setShowAddProfile] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [newProfile, setNewProfile] = useState({ pseudo: '', avatar: '🧑' });
  const [editingProfile, setEditingProfile] = useState(null);
  const [activeProfile, setActiveProfile] = useState(null);

  // Get token from localStorage
  const token = localStorage.getItem('spirify_token');
  
  // Check if user is premium
  const isPremium = user?.subscription_status === 'premium' || user?.subscription_status === 'active';

  // Translations helper
  const tf = t?.family || {};

  useEffect(() => {
    // Get active profile from localStorage
    const savedProfile = localStorage.getItem('spirify_active_profile');
    if (savedProfile) {
      setActiveProfile(JSON.parse(savedProfile));
    }
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      
      if (userData.subscription_status !== 'premium' && userData.subscription_status !== 'active') {
        setShowPremiumModal(true);
        setLoading(false);
      } else {
        fetchFamilyData(userData);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      navigate('/login');
    }
  };

  const fetchFamilyData = async (userData = user) => {
    if (!token || !userData?.id) return;
    
    try {
      setLoading(true);
      
      // Fetch profiles
      const profilesRes = await fetch(`${API}/api/family/profiles?user_id=${userData.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const profilesData = await profilesRes.json();
      setProfiles(profilesData.profiles || []);
      setCanAddProfile(profilesData.can_add);
      
      // Fetch rankings
      const rankingsRes = await fetch(`${API}/api/family/rankings?user_id=${userData.id}&timeframe=${rankingTimeframe}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const rankingsData = await rankingsRes.json();
      setRankings(rankingsData.rankings || []);
      
      // Fetch badges with language
      const badgesRes = await fetch(`${API}/api/family/badges?user_id=${userData.id}&language=${language}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const badgesData = await badgesRes.json();
      setBadges(badgesData.badges || []);
      setStats(badgesData.stats || {});
      
    } catch (error) {
      console.error('Error fetching family data:', error);
      toast.error(tf.error || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPremium && user) {
      fetchRankings();
    }
  }, [rankingTimeframe]);

  // Reload badges when language changes
  useEffect(() => {
    if (isPremium && user) {
      fetchFamilyData(user);
    }
  }, [language]);

  const fetchRankings = async () => {
    if (!token || !user?.id) return;
    try {
      const res = await fetch(`${API}/api/family/rankings?user_id=${user.id}&timeframe=${rankingTimeframe}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setRankings(data.rankings || []);
    } catch (error) {
      console.error('Error fetching rankings:', error);
    }
  };

  const handleAddProfile = async () => {
    if (!newProfile.pseudo.trim()) {
      toast.error(tf.pseudoRequired || 'Le pseudo est requis');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/family/profiles?user_id=${user.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newProfile)
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || tf.createError || 'Erreur lors de la création');
      }
      
      toast.success(tf.profileCreated || 'Profil créé !');
      setShowAddProfile(false);
      setNewProfile({ pseudo: '', avatar: '🧑' });
      fetchFamilyData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm(tf.deleteConfirm || 'Supprimer ce profil ? Cette action est irréversible.')) return;
    
    try {
      const res = await fetch(`${API}/api/family/profiles/${profileId}?user_id=${user.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error(tf.deleteError || 'Erreur lors de la suppression');
      
      toast.success(tf.profileDeleted || 'Profil supprimé');
      fetchFamilyData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSwitchProfile = (profile) => {
    localStorage.setItem('spirify_active_profile', JSON.stringify({
      id: profile.id,
      pseudo: profile.pseudo,
      avatar: profile.avatar,
      is_main: profile.is_main
    }));
    setActiveProfile(profile);
    toast.success(`${tf.switchedTo || 'Profil actif'}: ${profile.pseudo}`);
  };

  const handleEditProfile = (profile) => {
    setEditingProfile({
      id: profile.id,
      pseudo: profile.pseudo,
      avatar: profile.avatar,
      is_main: profile.is_main
    });
    setShowEditProfile(true);
  };

  const handleSaveEditProfile = async () => {
    if (!editingProfile?.pseudo?.trim()) {
      toast.error(tf.pseudoRequired || 'Le pseudo est requis');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/family/profiles/${editingProfile.id}?user_id=${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pseudo: editingProfile.pseudo,
          avatar: editingProfile.avatar
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || tf.updateError || 'Erreur lors de la mise à jour');
      }
      
      toast.success(tf.profileUpdated || 'Profil mis à jour !');
      setShowEditProfile(false);
      setEditingProfile(null);
      
      // Update active profile if it's the one being edited
      if (activeProfile && activeProfile.id === editingProfile.id) {
        const updatedActiveProfile = {
          ...activeProfile,
          pseudo: editingProfile.pseudo,
          avatar: editingProfile.avatar
        };
        localStorage.setItem('spirify_active_profile', JSON.stringify(updatedActiveProfile));
        setActiveProfile(updatedActiveProfile);
      }
      
      fetchFamilyData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const isActiveProfile = (profile) => {
    if (!activeProfile) return profile.is_main;
    return activeProfile.id === profile.id;
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Crown className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Crown className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 text-center text-gray-500 font-bold">{rank}</span>;
  };

  if (showPremiumModal) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <PremiumModal 
          isOpen={true} 
          onClose={() => navigate('/dashboard')}
          variant="generic"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <button 
            onClick={() => navigate('/dashboard')}
            className="text-cyan-400 hover:text-cyan-300 mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t?.lesson?.backToDashboard || 'Retour'}
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{tf.title || 'Famille'}</h1>
              <p className="text-slate-400 text-sm">{tf.subtitle || 'Apprenez ensemble, à votre rythme.'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-slate-800 rounded-lg p-1 mb-6">
            <TabsTrigger 
              value="members" 
              className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white rounded-md"
            >
              <Users className="w-4 h-4 mr-2" />
              {tf.members || 'Membres'}
            </TabsTrigger>
            <TabsTrigger 
              value="rankings"
              className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white rounded-md"
            >
              <Trophy className="w-4 h-4 mr-2" />
              {tf.rankings || 'Classement'}
            </TabsTrigger>
            <TabsTrigger 
              value="badges"
              className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white rounded-md"
            >
              <Award className="w-4 h-4 mr-2" />
              {tf.badges || 'Badges'}
            </TabsTrigger>
          </TabsList>

          {/* Members Tab */}
          <TabsContent value="members" className="space-y-4">
            {/* Header Card */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-white">{tf.groupTitle || 'Groupe famille'}</h3>
                    <p className="text-slate-400 text-sm">{tf.groupSubtitle || "Jusqu'à 4 profils sur le même compte."}</p>
                  </div>
                  <Badge variant="outline" className="border-cyan-500 text-cyan-400">
                    {profiles.length}/4 {tf.profilesLabel || 'profils'}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Member Cards */}
            {loading ? (
              <div className="text-center py-8 text-slate-400">{t?.settings?.loading || 'Chargement...'}</div>
            ) : profiles.length === 0 ? (
              <Card className="bg-slate-800/50 border-slate-700 border-dashed">
                <CardContent className="p-8 text-center">
                  <Users className="w-12 h-12 mx-auto text-slate-500 mb-3" />
                  <h3 className="font-semibold text-white mb-1">{tf.inviteFamily || 'Invite ta famille'}</h3>
                  <p className="text-slate-400 text-sm mb-4">
                    {tf.inviteMessage || 'Crée une petite compétition amicale pour apprendre plus régulièrement.'}
                  </p>
                  <Button 
                    onClick={() => setShowAddProfile(true)}
                    className="bg-cyan-600 hover:bg-cyan-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {tf.createProfile || 'Créer un profil'}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {profiles.map((profile) => (
                  <Card 
                    key={profile.id} 
                    className={`bg-slate-800/50 border-slate-700 cursor-pointer transition-all hover:border-cyan-500/50 ${
                      isActiveProfile(profile) ? 'border-green-500 ring-2 ring-green-500/30' : ''
                    }`}
                    onClick={() => handleSwitchProfile(profile)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <div className="text-4xl">{profile.avatar}</div>
                          {isActiveProfile(profile) && (
                            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs">✓</span>
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-white">{profile.pseudo}</span>
                            {profile.is_main && (
                              <Badge className="bg-cyan-600/20 text-cyan-400 text-xs">{tf.main || 'Principal'}</Badge>
                            )}
                            {isActiveProfile(profile) && (
                              <Badge className="bg-green-500/20 text-green-400 text-xs">{tf.active || 'Actif'}</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-slate-400">
                            <span className="flex items-center gap-1">
                              <Star className="w-3 h-3 text-yellow-500" />
                              {profile.xp_week} XP/{tf.week || 'sem'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Flame className="w-3 h-3 text-orange-500" />
                              {profile.streak} {tf.days || 'jours'}
                            </span>
                          </div>
                        </div>
                        
                        {/* Edit button - for all profiles */}
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditProfile(profile);
                            }}
                            className="text-slate-400 hover:text-cyan-400"
                            title={tf.edit || 'Modifier'}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {!profile.is_main && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteProfile(profile.id);
                              }}
                              className="text-slate-400 hover:text-red-400"
                              title={tf.delete || 'Supprimer'}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))} 
                
                {/* Add Profile Button */}
                {canAddProfile && (
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAddProfile(true)}
                    className="w-full border-dashed border-slate-600 text-slate-400 hover:border-cyan-500 hover:text-cyan-400 py-6"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {tf.addProfile || 'Ajouter un profil'}
                  </Button>
                )}
              </div>
            )}
          </TabsContent>

          {/* Rankings Tab */}
          <TabsContent value="rankings" className="space-y-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  {tf.familyRankings || 'Classement famille'}
                </CardTitle>
                <p className="text-slate-400 text-sm">{tf.rankingsObjective || 'Objectif : progresser ensemble, sans pression.'}</p>
              </CardHeader>
              <CardContent>
                {/* Timeframe Selector */}
                <div className="flex gap-2 mb-4">
                  {[
                    { id: 'weekly_xp', label: tf.week || 'Semaine' },
                    { id: 'monthly_xp', label: tf.month || 'Mois' },
                    { id: 'current_streak', label: tf.streak || 'Série' }
                  ].map(tf_item => (
                    <Button
                      key={tf_item.id}
                      variant={rankingTimeframe === tf_item.id ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setRankingTimeframe(tf_item.id)}
                      className={rankingTimeframe === tf_item.id 
                        ? 'bg-cyan-600 hover:bg-cyan-700' 
                        : 'border-slate-600 text-slate-400'
                      }
                    >
                      {tf_item.label}
                    </Button>
                  ))}
                </div>

                {/* Rankings List */}
                <div className="space-y-2">
                  {rankings.map((member) => (
                    <div 
                      key={member.id}
                      className={`flex items-center gap-3 p-3 rounded-lg ${
                        member.rank === 1 ? 'bg-yellow-500/10' : 'bg-slate-700/50'
                      }`}
                    >
                      <div className="w-8 flex justify-center">
                        {getRankIcon(member.rank)}
                      </div>
                      <div className="text-2xl">{member.avatar}</div>
                      <div className="flex-1">
                        <span className={`font-medium ${member.rank === 1 ? 'text-yellow-400' : 'text-white'}`}>
                          {member.pseudo}
                        </span>
                        {member.is_main && (
                          <span className="ml-2 text-xs text-slate-500">({tf.you || 'toi'})</span>
                        )}
                      </div>
                      <div className="text-right">
                        <span className={`font-bold ${member.rank === 1 ? 'text-yellow-400' : 'text-cyan-400'}`}>
                          {member.value}
                        </span>
                        <span className="text-slate-500 text-sm ml-1">
                          {rankingTimeframe === 'current_streak' ? (tf.daysShort || 'j') : 'XP'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Badges Tab */}
          <TabsContent value="badges" className="space-y-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Award className="w-5 h-5 text-purple-500" />
                  {tf.familyBadges || 'Badges famille'}
                </CardTitle>
                <p className="text-slate-400 text-sm">{tf.badgesMessage || 'Des récompenses pour la constance et l\'engagement.'}</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {badges.map((badge) => (
                    <div 
                      key={badge.id}
                      className={`flex items-center gap-4 p-4 rounded-lg ${
                        badge.earned 
                          ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30' 
                          : 'bg-slate-700/30 opacity-60'
                      }`}
                    >
                      <div className={`text-3xl ${badge.earned ? '' : 'grayscale'}`}>
                        {badge.id === 'badge_family_active' && '👨‍👩‍👧‍👦'}
                        {badge.id === 'badge_family_regular' && '📅'}
                        {badge.id === 'badge_family_engaged' && '🏆'}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-semibold ${badge.earned ? 'text-white' : 'text-slate-400'}`}>
                          {badge.title}
                        </h4>
                        <p className="text-sm text-slate-400">{badge.description}</p>
                      </div>
                      {badge.earned ? (
                        <Badge className="bg-green-500/20 text-green-400">✓ {tf.earned || 'Obtenu'}</Badge>
                      ) : (
                        <Badge variant="outline" className="border-slate-600 text-slate-500">{tf.toUnlock || 'À débloquer'}</Badge>
                      )}
                    </div>
                  ))}
                </div>

                {/* Family Stats */}
                <div className="mt-6 pt-4 border-t border-slate-700">
                  <h4 className="text-sm font-medium text-slate-400 mb-3">{tf.familyStats || 'Statistiques famille'}</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-700/30 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-cyan-400">{stats.total_profiles || 0}</div>
                      <div className="text-xs text-slate-400">{tf.totalMembers || 'Membres'}</div>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-yellow-400">{stats.total_xp || 0}</div>
                      <div className="text-xs text-slate-400">{tf.totalXP || 'XP cumulés'}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Profile Modal */}
      {showAddProfile && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <Card className="bg-slate-800 border-slate-700 w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-white">{tf.newProfile || 'Nouveau profil'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">{tf.pseudo || 'Pseudo'}</label>
                <input
                  type="text"
                  value={newProfile.pseudo}
                  onChange={(e) => setNewProfile({...newProfile, pseudo: e.target.value})}
                  placeholder={tf.pseudoPlaceholder || "Ex: Maman, Papa, Lucas..."}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
                  maxLength={20}
                />
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-2">{tf.avatar || 'Avatar'}</label>
                <div className="grid grid-cols-8 gap-2">
                  {AVATARS.map((avatar) => (
                    <button
                      key={avatar}
                      onClick={() => setNewProfile({...newProfile, avatar})}
                      className={`text-2xl p-2 rounded-lg transition-all ${
                        newProfile.avatar === avatar 
                          ? 'bg-cyan-600 ring-2 ring-cyan-400' 
                          : 'bg-slate-700 hover:bg-slate-600'
                      }`}
                    >
                      {avatar}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowAddProfile(false)}
                  className="flex-1 border-slate-600 text-slate-400"
                >
                  {tf.cancel || 'Annuler'}
                </Button>
                <Button 
                  onClick={handleAddProfile}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                >
                  {tf.create || 'Créer'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showEditProfile && editingProfile && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <Card className="bg-slate-800 border-slate-700 w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-white">{tf.editProfile || 'Modifier le profil'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">{tf.pseudo || 'Pseudo'}</label>
                <input
                  type="text"
                  value={editingProfile.pseudo}
                  onChange={(e) => setEditingProfile({...editingProfile, pseudo: e.target.value})}
                  placeholder={tf.pseudoPlaceholder || "Ex: Maman, Papa, Lucas..."}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
                  maxLength={20}
                />
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-2">{tf.avatar || 'Avatar'}</label>
                <div className="grid grid-cols-8 gap-2">
                  {AVATARS.map((avatar) => (
                    <button
                      key={avatar}
                      onClick={() => setEditingProfile({...editingProfile, avatar})}
                      className={`text-2xl p-2 rounded-lg transition-all ${
                        editingProfile.avatar === avatar 
                          ? 'bg-cyan-600 ring-2 ring-cyan-400' 
                          : 'bg-slate-700 hover:bg-slate-600'
                      }`}
                    >
                      {avatar}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowEditProfile(false);
                    setEditingProfile(null);
                  }}
                  className="flex-1 border-slate-600 text-slate-400"
                >
                  {tf.cancel || 'Annuler'}
                </Button>
                <Button 
                  onClick={handleSaveEditProfile}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                >
                  {tf.save || 'Enregistrer'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default FamilyPage;
